import { logError, logWarning } from '@core/logging'

export type Feature = 'monitoring'

function isFeature(feature: string): feature is Feature {
  return feature === 'monitoring'
}

interface Features {
  [key: string]: boolean
}

/**
 * Represents a map of features the depends on client's consent management
 * Used in local storage.
 */
export class ConsentFeatures {
  /**
   * Creates an instance of ConsentFeatures.
   * @param feaures - The list of features that are disabled.
   */
  constructor(private features: Features) {}

  /**
   * Get the last read message id from a MessagePreview instance.
   */
  getFeatures(): Features {
    return this.features
  }

  /**
   * Checks if the feature is disabled.
   */
  isDisabled(feature: Feature): boolean {
    return this.features[feature] === false
  }

  /**
   * Enables the features.
   */
  enableFeatures(features: Feature[]) {
    if (!features.length) {
      logWarning('Please supply a list of features in an array of string')
      return
    }

    const invalidFeatures = features.filter((f) => !isFeature(f))
    if (invalidFeatures.length) {
      logWarning('Enabling invalid features', {
        data: { features: invalidFeatures },
      })
    } else {
      this.features = {
        ...this.features,
        ...features.reduce((acc, f) => ({ ...acc, [f]: true }), {}),
      }
    }
  }

  /**
   * Disable the features.
   */
  disableFeatures(features: Feature[]) {
    if (!features.length) {
      logWarning('Please supply a list of features in an array of string')
      return
    }

    const invalidFeatures = features.filter((f) => !isFeature(f))
    if (invalidFeatures.length) {
      logWarning('Disabling invalid features', {
        data: { features: invalidFeatures },
      })
    } else {
      this.features = {
        ...this.features,
        ...features.reduce((acc, f) => ({ ...acc, [f]: false }), {}),
      }
    }
  }

  /**
   * Parses a JSON string and creates an instance of ConsentFeatures.
   */
  static fromJSON(json: string | undefined): ConsentFeatures | undefined {
    if (typeof json === 'undefined') {
      return undefined
    }

    try {
      const parsedData = JSON.parse(json)
      if (parsedData) {
        return new ConsentFeatures(parsedData)
      }
    } catch (error) {
      logError(new Error('Error parsing features disabled JSON: ' + error), {
        data: { json },
      })
    }

    return undefined
  }

  /**
   * Returns a JSON string representation of the ConsentFeatures instance.
   */
  toJSON(): string {
    return JSON.stringify(this.features)
  }
}
